import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { videoCategories, featuredVideos } from '@/mocks/videos';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const videosSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${SITE_URL}/videos`,
  url: `${SITE_URL}/videos`,
  name: '教学视频中心 - 高考考研全阶段课程 | 金榜旺财',
  description: '金榜旺财教学视频中心，涵盖小学至考研全阶段课程，CPA、护士执照、四六级、执业医师、法律资格等职业考试，名师在线教学，助力每位学生升学成功。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '教学视频', item: `${SITE_URL}/videos` },
    ],
  },
};

export default function VideosPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const id = 'schema-videos';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(videosSchema);
    document.title = '教学视频中心 - 高考考研全阶段课程 | 金榜旺财';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '金榜旺财教学视频中心，涵盖小学至考研全阶段课程，CPA、护士执照、四六级、执业医师、法律资格等职业考试，名师在线教学。');
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  const allVideos = [
    ...featuredVideos,
    {
      id: '9', title: '高中化学有机化学全解', teacher: '吴化学老师', category: '高中', grade: '高二',
      price: 22.9, originalPrice: 45.9, duration: '38分钟', students: 9870, rating: 4.8,
      thumbnail: 'https://public.readdy.ai/ai/img_res/fd611f5ad945cb08504ee71d9d2fc4bf.jpg',
    },
    {
      id: '10', title: '初中英语语法全套精讲', teacher: '郑英语老师', category: '初中', grade: '初三',
      price: 18.9, originalPrice: 38.9, duration: '42分钟', students: 14320, rating: 4.9,
      thumbnail: 'https://readdy.ai/api/search-image?query=Chinese%20English%20teacher%20teaching%20grammar%20to%20middle%20school%20students%2C%20bright%20classroom%20with%20English%20posters%2C%20engaging%20atmosphere&width=400&height=280&seq=v010&orientation=landscape',
    },
    {
      id: '11', title: '小学数学奥数入门班', teacher: '黄奥数老师', category: '小学', grade: '四年级',
      price: 12.9, originalPrice: 25.9, duration: '25分钟', students: 28900, rating: 5.0,
      thumbnail: 'https://readdy.ai/api/search-image?query=Chinese%20primary%20school%20math%20olympiad%20teacher%20with%20colorful%20math%20puzzles%20and%20diagrams%2C%20cheerful%20classroom%20environment&width=400&height=280&seq=v011&orientation=landscape',
    },
    {
      id: '12', title: '托福口语满分技巧', teacher: '方口语老师', category: '英语/托福/雅思', grade: '大学',
      price: 45.9, originalPrice: 89.9, duration: '48分钟', students: 5670, rating: 4.8,
      thumbnail: 'https://readdy.ai/api/search-image?query=English%20speaking%20coach%20teaching%20TOEFL%20speaking%20skills%2C%20modern%20studio%20setting%20with%20microphone%20and%20notes%2C%20professional%20atmosphere&width=400&height=280&seq=v012&orientation=landscape',
    },
  ];

  const filtered = allVideos.filter((v) => {
    const matchCat = activeCategory === 'all' || v.category === videoCategories.find(c => c.id === activeCategory)?.label;
    const matchSearch = v.title.includes(searchQuery) || v.teacher.includes(searchQuery);
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />

      {/* Page Header */}
      <div className="relative pt-20 pb-12 bg-gradient-to-br from-red-700 to-red-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20educational%20pattern%20with%20books%20and%20graduation%20caps%2C%20dark%20red%20background%2C%20subtle%20texture&width=1440&height=300&seq=vh001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">教学视频中心</h1>
          <p className="text-white/70 text-sm mb-8">涵盖小学至考研全阶段 · CPA · 护士执照 · 四六级 · 执业医师 · 法律资格</p>
          {/* Search */}
          <div className="max-w-lg mx-auto flex items-center bg-white rounded-full px-4 py-2.5 gap-3">
            <i className="ri-search-line text-gray-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索课程、老师..."
              className="flex-1 outline-none text-sm text-gray-700 bg-transparent"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeCategory === 'all' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-red-50 hover:text-red-600'
            }`}
          >
            全部课程
          </button>
          {videoCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeCategory === cat.id ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              <i className={cat.icon}></i>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <p className="text-gray-500 text-sm mb-6">共找到 <strong className="text-gray-900">{filtered.length}</strong> 门课程</p>

        {/* Video Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((video) => (
            <Link key={video.id} to={`/videos/${video.id}`} className="bg-white rounded-2xl overflow-hidden group cursor-pointer hover:scale-[1.02] transition-all block">
              <div className="relative overflow-hidden h-44">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/90 rounded-full">
                    <i className="ri-play-fill text-red-600 text-xl"></i>
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-red-600 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                  {video.category}
                </div>
                <div className="absolute bottom-3 left-3 text-white text-xs flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 text-sm leading-snug mb-2 line-clamp-2">{video.title}</h3>
                <p className="text-gray-400 text-xs mb-3">{video.teacher} · {video.grade}</p>
                <div className="flex items-center gap-1 mb-3">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-700">{video.rating}</span>
                  <span className="text-xs text-gray-400">({video.students.toLocaleString()}人学习)</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-red-600 font-bold text-base">¥{video.price}</span>
                    <span className="text-gray-300 text-xs line-through">¥{video.originalPrice}</span>
                  </div>
                  <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 text-white whitespace-nowrap">
                    查看详情
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-video-off-line text-5xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-400">暂无相关课程，请尝试其他关键词</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
